import React, {
  useState,
  useEffect,
  useRef,
  useReducer,
  useContext,
  useMemo,
} from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Input,
  IconButton,
  Icon,
  useToast,
  Spinner,
  Tooltip,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import CSSAvatar from "./CSSAvatar.jsx";
import {IoArrowBack, IoSettingsOutline} from "react-icons/io5";
import {FiFile, FiImage, FiHash, FiUsers} from "react-icons/fi";
import {BsDot, BsThreeDots} from "react-icons/bs";
import {MdDelete} from "react-icons/md";
import {format, isToday, isYesterday, isSameDay} from "date-fns";
import {Node} from "slate";
import {chatReducer, initialChatState} from "../additional_files/chatReducer";
import {useChatSocket} from "../additional_files/custom";
import {crud, url} from "../crudRequests";
import {crudStorageUpload, getFileUrl} from "../storageRequests";
import {fileProxy} from "./helpers/fileProxy";
import {MessagingContext, GroupManagementContext} from "./PatientChat";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import FormattedText from "./FormattedText";
import MessagingFilePreview from "./MessagingFilePreview";

export default function StaffChatArea({
  user,
  onBack,
  loadConversations,
  isPatientView = false,
  currentUserId: propCurrentUserId,
  schState: propSchState,
  openGroupManagement: propOpenGroupManagement,
  canSendMessage = true,
}) {
  // Try to get from context first, fall back to props
  const contextValue = useContext(MessagingContext);
  const groupContextValue = useContext(GroupManagementContext);

  const schState = contextValue?.schState || propSchState;
  const openGroupManagement =
    groupContextValue?.openGroupManagement || propOpenGroupManagement;
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const toast = useToast();

  // Refs
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // State
  const [inputValue, setInputValue] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [isSending, setIsSending] = useState(false);

  // Get conversation data from state
  const {conversations, selectedChat, loading, messages = []} = state;

  // Get current user ID - support both patient and staff contexts
  const currentUserId =
    propCurrentUserId ||
    schState.doctor?.did ||
    schState.admin?.id ||
    schState.patient?.pid;

  // Always use the conversationId from the user object (it should always have one)
  const conversationId = user.conversationId;

  // Memoize rooms array to prevent unnecessary reconnections
  const rooms = React.useMemo(() => [conversationId], [conversationId]);

  // Initialize socket connection - passing patient object or creating a minimal one
  const patientObj = schState.patient || {pid: currentUserId};
  const socket = useChatSocket(url + "/chat", rooms, patientObj, dispatch);

  // Load conversation on mount or user change
  useEffect(() => {
    if (user && currentUserId && conversationId) {
      loadConversation();
    }
  }, [user?.id, currentUserId, conversationId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, selectedChat]);

  // Load conversation data
  const loadConversation = async () => {
    dispatch({type: "SET_LOADING", payload: true});

    try {
      const response = await crud(schState, [
        {
          db: schState.db || schState.dbName,
          collection: "conversations",
          parameters: [{conversationId}],
          method: "findOne",
        },
      ]);

      if (response.data?.[0]) {
        const conversation = Array.isArray(response.data[0])
          ? response.data[0][0]
          : response.data[0];

        if (conversation && conversation.conversationId) {
          let messagesToShow = conversation.messages || [];

          // For groups, filter messages based on user's messageVisibilityStart
          if (conversation.isGroup && conversation.usersMeta?.[currentUserId]) {
            const visibilityStart =
              conversation.usersMeta[currentUserId].messageVisibilityStart || 0;
            messagesToShow = messagesToShow.filter((msg) => {
              const msgTime = new Date(msg.timestamp).getTime();
              return msgTime >= visibilityStart;
            });
          }

          // Sort messages by timestamp
          const sortedMessages = messagesToShow.sort((a, b) => {
            return new Date(a.timestamp) - new Date(b.timestamp);
          });

          dispatch({type: "SET_SELECTED_CHAT", payload: conversation});
          dispatch({type: "SET_MESSAGES", payload: sortedMessages});
        } else {
          createNewConversationObject();
        }
      } else {
       // createNewConversationObject(); no need to create new conversation object if it doesn't exist because the conversation object is already set in the parent component

        dispatch({type: "SET_SELECTED_CHAT", payload: user});
        dispatch({type: "SET_MESSAGES", payload: []});
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
      console.error("Error details:", {
        conversationId,
        schState,
        error: error.message,
      });
      toast({
        title: "Error loading messages",
        status: "error",
        duration: 3000,
      });
    } finally {
      dispatch({type: "SET_LOADING", payload: false});
    }
  };

  // Helper function to create new conversation object
  const createNewConversationObject = () => {
    // Get current user info
    const currentUserInfo = schState.doctor
      ? {
          name: `${schState.doctor.firstName || schState.doctor.name} ${
            schState.doctor.lastName || ""
          }`.trim(),
          photo: schState.doctor.photo || schState.doctor.img,
          userType: "doctor",
        }
      : schState.admin
      ? {
          name:
            schState.admin.name ||
            `${schState.admin.firstName} ${schState.admin.lastName}`,
          photo: schState.admin.photo || schState.admin.img,
          userType: "admin",
        }
      : schState.patient
      ? {
          name: `${schState.patient.fName} ${schState.patient.lName}`.trim(),
          photo: schState.patient.img,
          userType: "patient",
        }
      : null;

    // Create new conversation if doesn't exist
    const isPatientChat = !!schState.patient; // If we have patient context, we're in Chat tab
    const newConversation = {
      conversationId,
      usersId: [currentUserId, user.id],
      messages: [],
      timestamp: Date.now(),
      // Only add context field when creating from patient Chat tab
      ...(isPatientChat && {context: "patient-chat"}),
      usersMeta: {
        [user.id]: {
          name: user.displayName,
          photo: user.photo,
          userType: user.userType,
        },
        [currentUserId]: currentUserInfo,
      },
    };

    dispatch({type: "SET_SELECTED_CHAT", payload: newConversation});
    dispatch({type: "SET_MESSAGES", payload: []});
  };

  // Create new conversation in database
  const createConversation = async () => {
    // Groups are already created, don't recreate them
    if (user.isGroup) {
      return;
    }

    try {
   /*   const timestamp = Date.now();

      // Get current user info
      const currentUserInfo = schState.doctor
        ? {
            name: `${schState.doctor.firstName || schState.doctor.name} ${
              schState.doctor.lastName || ""
            }`.trim(),
            photo: schState.doctor.photo || schState.doctor.img,
            type: "doctor",
          }
        : schState.admin
        ? {
            name:
              schState.admin.name ||
              `${schState.admin.firstName} ${schState.admin.lastName}`,
            photo: schState.admin.photo || schState.admin.img,
            type: "admin",
          }
        : schState.patient
        ? {
            name: `${schState.patient.fName} ${schState.patient.lName}`.trim(),
            photo: schState.patient.img,
            type: "patient",
          }
        : null;

      const isPatientChat = !!schState.patient; // If we have patient context, we're in Chat tab

      const conversation = {
        conversationId,
        usersId: [currentUserId, user.id],
        messages: [],
        timestamp: timestamp,
        // Only add context field when creating from patient Chat tab
        ...(isPatientChat && {context: "patient-chat"}),
        usersMeta: {
          [user.id]: {
            name: user.displayName,
            photo: user.photo,
            type: user.userType,
          },
          [currentUserId]: currentUserInfo,
        },
      };*/

      //conversation object is already set in the parent component
      const conversation = user;
      // Build requests
      const requests = [
        {
          db: schState.db || schState.dbName,
          collection: "conversations",
          parameters: [conversation],
          method: "insertOne",
        },
      ];

      // Update chatsId for both users
      const userCollection  = (userMeta)=>{
         let idKey = '';
         let collection = '';
        switch(userMeta.userType){
          case "doctor":
            idKey = "did";
            collection = "doctors";
            break;
          case "admin":
            idKey = "id";
            collection = "admins";
            break;
          case "patient":
            idKey = "pid";
            collection = "patients";
            break;
          default:
            return null;
        }
        return {idKey, collection};
      };

      Object.entries(conversation.usersMeta).forEach(([userId, userMeta])=>{
        const {idKey, collection} = userCollection(userMeta);
        requests.push({
          db: schState.db || schState.dbName,
          collection: collection,
          parameters: [{[idKey]: userId}, {$addToSet: {chatsId: conversationId}}],
          method: "updateOne",
        });
      });


      // Send requests with socket notification through crud. The member of
      // staff is told in their personal room — `conversation` is the object
      // built for the chat and carries no `id`, so `user.id` addressed
      // `user_undefined` and nobody ever heard of a new chat.
      await crud(schState, requests, {
        socket: {
          rooms: conversation.usersId
            .filter((memberId) => memberId !== currentUserId)
            .map((memberId) => `user_${memberId}`),
          namespace: "/chat",
          arguments: ["new-conversation", conversation],
        },
      });

   

      // Join the newly created conversation room for real-time messages
      if (socket.current && socket.current.connected) {
        socket.current.emit("join-room", conversationId);
      }

      // DON'T call loadConversations here - it causes duplicate loads
      // The parent will receive "new-conversation" socket event and update automatically
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast({
        title: "Error creating conversation",
        description: "Please try again",
        status: "error",
        duration: 3000,
      });
      throw error; // Re-throw to prevent message sending
    }
  };

  // Send message (text or files)
  const handleSendMessage = async (messageContent) => {
    if (!canSendMessage) {
      toast({
        title: "Messaging unavailable",
        description: "You can only respond to conversations sent to you.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    // Prevent sending if already sending
    if (isSending) return;

    let content = messageContent;
    if (typeof messageContent !== "string") {
      // Serialize Slate content to string
      content = messageContent?.map((n) => Node.string(n)).join("\n") || "";
    }

    // Set sending state
    setIsSending(true);

    try {
      // Check if conversation exists ONCE before sending anything
      // This prevents duplicate conversation creation when sending text + files
      const isNewConversation =
        (!messages || messages.length === 0) &&
        (!selectedChat || !selectedChat._id);

      if (isNewConversation) {
        await createConversation();

        // Refresh parent's conversation list ONLY for new conversations
        // This adds the conversation to the sidebar
        if (loadConversations) {
          loadConversations();
        }
      }

      // If files are selected, send files
      if (selectedFiles.length > 0) {
        await handleSendFiles();
        // If there's also text, send it as a separate message
        if (content?.trim()) {
          await handleSendTextMessage(content);
        }
      } else if (content?.trim()) {
        // Otherwise send text
        await handleSendTextMessage(content);
      }
    } finally {
      // Re-enable sending after a brief delay to prevent rapid clicks
      setTimeout(() => setIsSending(false), 500);
    }
  };

  // Send text message
  const handleSendTextMessage = async (textToSend) => {
    if (!textToSend?.trim()) return;

    // Conversation should already exist (created in handleSendMessage)
    // No need to check again here

    const newMessage = {
      db: schState.db || schState.dbName,
      lid: schState.selectedLocation,
      conversationId,
      senderId: currentUserId,
      userType: 'patient',
      messageType: "text",
      message: textToSend.trim(),
      timestamp: new Date().toISOString(),
    };

 
    // Emit through socket for real-time delivery
    if (socket.current && socket.current.connected) {
      // Use staff-message event like in Messaging and Chat
      socket.current.emit("staff-message", newMessage);

      // Don't add temporary message - wait for server confirmation
      // The server will emit back the message with proper ID via "message" event
    } else {
      console.error("Socket not connected!");
      toast({
        title: "Connection error",
        description: "Unable to send message. Please refresh the page.",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Format message time
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);

    if (isToday(date)) {
      return format(date, "h:mm a");
    } else if (isYesterday(date)) {
      return `Yesterday at ${format(date, "h:mm a")}`;
    } else {
      return format(date, "MMM d 'at' h:mm a");
    }
  };

  // Format date divider
  const formatDateDivider = (date) => {
    if (isToday(date)) {
      return "Today";
    } else if (isYesterday(date)) {
      return "Yesterday";
    } else {
      return format(date, "EEEE, MMMM d");
    }
  };

  // Check if user can delete a message (within 30 minutes of sending)
  const canDeleteMessage = (message) => {
    if (!message || !message.messageId) return false;

    // Only allow deleting own messages
    const isOwner = message.senderId === currentUserId;

    // Add time limit - can only delete within 30 minutes
    const timeDiff = Date.now() - new Date(message.timestamp).getTime();
    return isOwner && timeDiff <= 30 * 60 * 1000; // 30 minutes
  };

  // Handle delete message
  const handleDeleteMessage = async (messageId) => {
    // Emit delete-message event via socket
    if (socket && socket.current && socket.current.connected) {
      socket.current.emit("delete-message", {
        messageId,
        conversationId,
        db: schState.db || schState.dbName,
      });

      toast({
        title: "Message deleted",
        status: "success",
        duration: 2000,
      });
    } else {
      console.error("Socket not connected");
      toast({
        title: "Failed to delete message",
        description: "Connection error",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    if (!fileType) return FiFile;
    if (fileType.startsWith("image/")) return FiImage;
    return FiFile;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = new Date(message.timestamp);
      if (!currentDate || !isSameDay(currentDate, messageDate)) {
        currentDate = messageDate;
        groups.push({
          date: messageDate,
          messages: [message],
        });
      } else {
        groups[groups.length - 1].messages.push(message);
      }
    });

    return groups;
  };

  // Handle sending files
  const handleSendFiles = async () => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    setUploadingFile(true);

    try {
      // Conversation should already exist (created in handleSendMessage)
      // No need to check again here

      // Upload all files
      const uploadedFiles = [];
      const failedFiles = [];

      for (const selectedFile of selectedFiles) {
        try {
          // Create a unique filename with timestamp
          const timestamp = new Date().getTime();
          const fileSize = selectedFile.size / 1000;
          const fileName = `prefix_${timestamp}-${fileSize}-${selectedFile.name}`;

          // Create a new File with the unique name
          const fileToUpload = new File([selectedFile], fileName, {
            type: selectedFile.type,
          });

          // Upload file to storage
          const uploadResponse = await crudStorageUpload(
            schState,
            fileToUpload,
            "brightlighthealth", // Fixed bucket name
            `messages/${conversationId}/` // Messages directory for chat files
          );

          if (!uploadResponse || !uploadResponse.data) {
            throw new Error(`Upload failed for ${selectedFile.name}`);
          }

          uploadedFiles.push({
            path: `messages/${conversationId}/`,
            fileName: fileName,
            originalName: selectedFile.name,
            type: selectedFile.type,
            size: selectedFile.size,
          });
        } catch (error) {
          console.error(`Failed to upload ${selectedFile.name}:`, error);
          failedFiles.push(selectedFile.name);
        }
      }

      if (uploadedFiles.length === 0) {
        throw new Error("All files failed to upload");
      }

      // Send each file as a separate message
      for (const uploadedFile of uploadedFiles) {
        const fileMessage = {
          db: schState.db || schState.dbName,
          lid: schState.selectedLocation,
          conversationId,
          senderId: currentUserId,
          userType: 'patient',
          messageType: "file",
          message: uploadedFile.originalName,
          attachment: uploadedFile,
          timestamp: new Date().toISOString(),
        };

        // Send via socket
        if (socket.current && socket.current.connected) {
          socket.current.emit("staff-message", fileMessage);
        }
      }

      // Clear selected files
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Show success toast
      if (failedFiles.length > 0) {
        toast({
          title: "Some files uploaded",
          description: `Failed: ${failedFiles.join(", ")}`,
          status: "warning",
          duration: 4000,
        });
      } else {
        toast({
          title: "Files sent successfully",
          status: "success",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error("Error sending files:", error);
      toast({
        title: "Failed to send files",
        description: error.message,
        status: "error",
        duration: 4000,
      });
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file select
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  // Handle files dropped via drag and drop
  const handleFilesDropped = (fileList) => {
    const files = Array.from(fileList);
    processFiles(files);
  };

  // Process files (shared logic for both file input and drag/drop)
  const processFiles = (files) => {
    // Check if we're adding too many files (limit to 10 total)
    if (selectedFiles.length + files.length > 10) {
      toast({
        title: "Too many files",
        description: "You can only send up to 10 files at once",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Validate each file
    const validFiles = [];
    for (const file of files) {
      // Check file size (10MB limit per file)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: `File too large: ${file.name}`,
          description: "Each file must be smaller than 10MB",
          status: "error",
          duration: 3000,
        });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  let clinicianData = useMemo(() => {
    if (user.isGroup) return null;
    const entries = Object.entries(user.usersMeta || {});
    const other = entries.find(([userId]) => userId !== currentUserId);
    return other ? other[1] : null;
  }, [user, currentUserId])

  return (
    <Flex h="100%" w="100%" direction="column" bg="white" position="relative">
      {/* Header */}
      <HStack
        px={5}
        py={2.5}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="white"
        spacing={3}
        h="52px"
        flexShrink={0}
      >
        <IconButton
          icon={<IoArrowBack />}
          size="sm"
          variant="ghost"
          color="gray.500"
          display={{base: "flex", md: "none"}}
          onClick={onBack}
          aria-label="Back"
        />

        {/* Channel/User Info */}
        <HStack flex={1} spacing={2.5}>
          {user.isGroup ? (
            <>
              <Flex
                w="32px"
                h="32px"
                bg="#f0f0ff"
                rounded="lg"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Icon as={FiHash} color="#5754FF" fontSize="15px" />
              </Flex>
              <Box>
                <Text fontSize="15px" fontWeight="600" color="#1a1f36" lineHeight="1.2">
                  {user.displayName}
                </Text>
                <Text fontSize="11px" color="gray.400" lineHeight="1.2">
                  {user.memberCount} members
                </Text>
              </Box>
            </>
          ) : (
            <>
              <CSSAvatar user={clinicianData} size="xs" />
              <Box>
                <Text fontSize="15px" fontWeight="600" color="#1a1f36" lineHeight="1.2">
                  {clinicianData?.name}
                </Text>
                <Text fontSize="11px" color="gray.400" lineHeight="1.2">
                  Direct message
                </Text>
              </Box>
            </>
          )}
        </HStack>

        {/* Header Actions */}
        {user.isGroup && openGroupManagement && (
          <Tooltip
            label={
              user.members?.[currentUserId]?.isAdmin
                ? "Manage group"
                : "Group info"
            }
          >
            <IconButton
              icon={<IoSettingsOutline />}
              size="sm"
              variant="ghost"
              color="gray.400"
              _hover={{color: "#5754FF", bg: "#f0f0ff"}}
              onClick={() => openGroupManagement(user)}
              aria-label="Group info"
            />
          </Tooltip>
        )}
      </HStack>

      {/* Messages Area */}
      <Box
        ref={scrollRef}
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        px={{base: 4, md: 6}}
        py={5}
        bg="#fafafa"
        minH={0}
        className="sbar3"
      >
        {loading ? (
          <Flex justify="center" align="center" h="full">
            <Spinner size="md" color="#5754FF" thickness="2px" />
          </Flex>
        ) : !messages || messages.length === 0 ? (
          <Flex justify="center" align="center" h="full">
            <VStack spacing={2}>
              <Flex
                w="48px"
                h="48px"
                bg="#f0f0ff"
                rounded="full"
                align="center"
                justify="center"
                mb={1}
              >
                <Icon as={FiHash} color="#5754FF" fontSize="20px" />
              </Flex>
              <Text color="#1a1f36" fontSize="15px" fontWeight="600">
                Start a conversation
              </Text>
              <Text color="gray.400" fontSize="13px" textAlign="center">
                Send a message to {clinicianData?.name || user.displayName}
              </Text>
            </VStack>
          </Flex>
        ) : (
          <VStack spacing={0} align="stretch">
            {groupMessagesByDate(messages).map((group, groupIndex) => (
              <Box key={groupIndex}>
                {/* Date Divider */}
                <Flex align="center" my={5} gap={3}>
                  <Divider borderColor="gray.200" />
                  <Text
                    fontSize="11px"
                    color="gray.400"
                    px={3}
                    py={1}
                    bg="white"
                    rounded="full"
                    flexShrink={0}
                    fontWeight="500"
                    borderWidth="1px"
                    borderColor="gray.100"
                    letterSpacing="0.02em"
                  >
                    {formatDateDivider(group.date)}
                  </Text>
                  <Divider borderColor="gray.200" />
                </Flex>

                {/* Messages for this date */}
                {group.messages.map((message, index) => {
                  const isOwn = message.senderId === currentUserId;
                  const showAvatar =
                    index === 0 ||
                    group.messages[index - 1]?.senderId !== message.senderId;

                  // Check if this is a new message from same user after time gap (5 minutes)
                  const isNewAfterGap =
                    index > 0 &&
                    !showAvatar &&
                    group.messages[index - 1]?.senderId === message.senderId &&
                    new Date(message.timestamp) -
                      new Date(group.messages[index - 1]?.timestamp) >
                      300000;

                  // Check if next message is from different user or if this is the last message
                  const isLastInGroup =
                    index === group.messages.length - 1 ||
                    group.messages[index + 1]?.senderId !== message.senderId;

                  // Bubble border radius — round all corners except the corner closest to the sender
                  const ownRadius = showAvatar
                    ? "18px 18px 4px 18px"
                    : isLastInGroup
                    ? "18px 4px 18px 18px"
                    : "18px 4px 4px 18px";
                  const otherRadius = showAvatar
                    ? "18px 18px 18px 4px"
                    : isLastInGroup
                    ? "4px 18px 18px 18px"
                    : "4px 18px 18px 4px";

                  return (
                    <Flex
                      key={message.messageId || index}
                      justify={isOwn ? "flex-end" : "flex-start"}
                      mb={isLastInGroup ? 4 : 1}
                      mt={isNewAfterGap ? 4 : 0}
                      className="message-fade-in"
                    >
                      <Flex
                        maxW={{base: "85%", md: "65%"}}
                        direction={isOwn ? "row-reverse" : "row"}
                        align="flex-end"
                        gap={2}
                      >
                        {/* Avatar — only for other users */}
                        {!isOwn && (
                          <Box w="30px" flexShrink={0} mb="2px">
                            {showAvatar || isNewAfterGap ? (
                              <CSSAvatar
                                user={
                                  user.isGroup &&
                                  user.members?.[message.senderId]
                                    ? user.members[message.senderId]
                                    : clinicianData
                                }
                                size="sm"
                              />
                            ) : (
                              <Box w="30px" />
                            )}
                          </Box>
                        )}

                        {/* Message Content */}
                        <VStack align={isOwn ? "flex-end" : "flex-start"} spacing={0.5} role="group">
                          {/* Sender name for group chats or first in sequence */}
                          {!isOwn && (showAvatar || isNewAfterGap) && (
                            <Text
                              fontSize="12px"
                              fontWeight="600"
                              color="gray.500"
                              px={1}
                              mb={0.5}
                            >
                              {user.members?.[message.senderId]?.name || clinicianData?.name || user.displayName}
                            </Text>
                          )}

                          {/* File message */}
                          {message.messageType === "file" ||
                          message.messageType === "files" ? (
                            message.isDeleted ? (
                              <Box
                                px={4}
                                py={2.5}
                                bg={isOwn ? "#eeeeff" : "white"}
                                borderRadius={isOwn ? ownRadius : otherRadius}
                                borderWidth={isOwn ? 0 : "1px"}
                                borderColor="gray.100"
                              >
                                <Text
                                  fontStyle="italic"
                                  color="gray.400"
                                  fontSize="13px"
                                >
                                  File deleted
                                </Text>
                              </Box>
                            ) : (
                              <Flex align="center" gap={1}>
                                {/* Delete menu — before bubble for own messages */}
                                {isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                  <Menu placement="bottom-end">
                                    <MenuButton
                                      as={IconButton}
                                      icon={<BsThreeDots />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.300"
                                      opacity={0}
                                      _groupHover={{opacity: 1}}
                                      transition="opacity 0.15s"
                                      _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                      aria-label="Message options"
                                      h="20px"
                                      w="20px"
                                      minW="20px"
                                    />
                                    <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                      <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                        Delete
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                )}
                                <Box minW="220px" maxW="320px">
                                  <Tooltip label="Click to preview" fontSize="xs" placement="top">
                                    <HStack
                                      px={4}
                                      py={3}
                                      bg={isOwn ? "#5754FF" : "white"}
                                      borderRadius={isOwn ? ownRadius : otherRadius}
                                      borderWidth={isOwn ? 0 : "1px"}
                                      borderColor="gray.100"
                                      spacing={3}
                                      cursor="pointer"
                                      _hover={{
                                        bg: isOwn ? "#4644e0" : "gray.50",
                                      }}
                                      transition="all 0.15s"
                                      onClick={async () => {
                                        if (
                                          message.attachment?.path &&
                                          message.attachment?.fileName
                                        ) {
                                          try {
                                            const response = await fileProxy(
                                              schState,
                                              "brightlighthealth",
                                              message.attachment.path,
                                              message.attachment.fileName
                                            );

                                            if (response && response.data) {
                                              const blob = new Blob(
                                                [response.data],
                                                {
                                                  type:
                                                    response.headers?.[
                                                      "content-type"
                                                    ] ||
                                                    "application/octet-stream",
                                                }
                                              );
                                              const blobUrl =
                                                URL.createObjectURL(blob);

                                              setFilePreview({
                                                fileName:
                                                  message.attachment.originalName ||
                                                  message.attachment.fileName,
                                                fileUrl: blobUrl,
                                                conversationId,
                                                isBlob: true,
                                              });
                                            } else {
                                              if (message.attachment?.url) {
                                                setFilePreview({
                                                  fileName:
                                                    message.attachment.originalName ||
                                                    message.attachment.fileName,
                                                  fileUrl: message.attachment.url,
                                                  conversationId,
                                                });
                                              } else {
                                                toast({
                                                  title: "Unable to preview file",
                                                  description: "The file may not be available.",
                                                  status: "error",
                                                  duration: 5000,
                                                });
                                              }
                                            }
                                          } catch (error) {
                                            console.error("Error getting file URL:", error);
                                            toast({
                                              title: "Failed to open file",
                                              status: "error",
                                              duration: 3000,
                                            });
                                          }
                                        }
                                      }}
                                    >
                                      <Icon
                                        as={getFileIcon(message.attachment?.type)}
                                        color={isOwn ? "whiteAlpha.800" : "gray.400"}
                                        fontSize="20px"
                                        flexShrink={0}
                                      />
                                      <VStack align="start" spacing={0} flex={1}>
                                        <Text
                                          fontSize="13px"
                                          fontWeight="500"
                                          color={isOwn ? "white" : "#1a1f36"}
                                          noOfLines={1}
                                        >
                                          {message.attachment?.originalName ||
                                            message.attachment?.name ||
                                            message.message}
                                        </Text>
                                        <Text fontSize="11px" color={isOwn ? "whiteAlpha.700" : "gray.400"}>
                                          {message.attachment?.size
                                            ? formatFileSize(message.attachment.size)
                                            : "File"}
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </Tooltip>
                                </Box>
                                {/* Delete menu — after bubble for other messages */}
                                {!isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                  <Menu placement="bottom-end">
                                    <MenuButton
                                      as={IconButton}
                                      icon={<BsThreeDots />}
                                      size="xs"
                                      variant="ghost"
                                      color="gray.300"
                                      opacity={0}
                                      _groupHover={{opacity: 1}}
                                      transition="opacity 0.15s"
                                      _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                      aria-label="Message options"
                                      h="20px"
                                      w="20px"
                                      minW="20px"
                                    />
                                    <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                      <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                        Delete
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                )}
                              </Flex>
                            )
                          ) : (
                            /* Regular text message */
                            <Flex align="center" gap={1}>
                              {/* Delete menu — before bubble for own messages */}
                              {isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                <Menu placement="bottom-end">
                                  <MenuButton
                                    as={IconButton}
                                    icon={<BsThreeDots />}
                                    size="xs"
                                    variant="ghost"
                                    color="gray.300"
                                    opacity={0}
                                    _groupHover={{opacity: 1}}
                                    transition="opacity 0.15s"
                                    _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                    aria-label="Message options"
                                    h="20px"
                                    w="20px"
                                    minW="20px"
                                  />
                                  <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                    <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                      Delete
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              )}
                              {message.isDeleted ? (
                                <Box
                                  px={4}
                                  py={2.5}
                                  bg={isOwn ? "#eeeeff" : "white"}
                                  borderRadius={isOwn ? ownRadius : otherRadius}
                                  borderWidth={isOwn ? 0 : "1px"}
                                  borderColor="gray.100"
                                >
                                  <Text
                                    fontStyle="italic"
                                    color="gray.400"
                                    fontSize="13px"
                                  >
                                    Message deleted
                                  </Text>
                                </Box>
                              ) : (
                                <Box
                                  px={4}
                                  py={2.5}
                                  bg={isOwn ? "#5754FF" : "white"}
                                  borderRadius={isOwn ? ownRadius : otherRadius}
                                  borderWidth={isOwn ? 0 : "1px"}
                                  borderColor="gray.100"
                                  fontSize="14px"
                                  lineHeight="1.5"
                                  color={isOwn ? "white" : "#1a1f36"}
                                  boxShadow={isOwn ? "none" : "0 1px 2px rgba(0,0,0,0.04)"}
                                >
                                  <FormattedText
                                    text={message.message}
                                    color={isOwn ? "white" : "#1a1f36"}
                                  />
                                </Box>
                              )}
                              {/* Delete menu — after bubble for other messages */}
                              {!isOwn && canDeleteMessage(message) && !message.isDeleted && (
                                <Menu placement="bottom-end">
                                  <MenuButton
                                    as={IconButton}
                                    icon={<BsThreeDots />}
                                    size="xs"
                                    variant="ghost"
                                    color="gray.300"
                                    opacity={0}
                                    _groupHover={{opacity: 1}}
                                    transition="opacity 0.15s"
                                    _hover={{bg: "gray.100", color: "gray.500", opacity: 1}}
                                    aria-label="Message options"
                                    h="20px"
                                    w="20px"
                                    minW="20px"
                                  />
                                  <MenuList minW="140px" fontSize="13px" borderColor="gray.200" boxShadow="sm">
                                    <MenuItem icon={<MdDelete />} color="red.500" _hover={{bg: "red.50"}} onClick={() => handleDeleteMessage(message.messageId)}>
                                      Delete
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              )}
                            </Flex>
                          )}

                          {/* Timestamp — shown on last message in a group */}
                          {isLastInGroup && (
                            <Text
                              fontSize="10px"
                              color="gray.400"
                              px={1}
                              mt={0.5}
                            >
                              {formatMessageTime(message.timestamp)}
                            </Text>
                          )}
                        </VStack>
                      </Flex>
                    </Flex>
                  );
                })}
              </Box>
            ))}

            {otherUserTyping && (
              <Flex justify="flex-start" mt={2}>
                <Flex align="flex-end" gap={2}>
                  <Box w="30px" flexShrink={0}>
                    <CSSAvatar user={clinicianData || user} size="sm" />
                  </Box>
                  <Box
                    px={4}
                    py={2.5}
                    bg="white"
                    borderRadius="18px 18px 18px 4px"
                    borderWidth="1px"
                    borderColor="gray.100"
                  >
                    <HStack spacing={1}>
                      <Box className="typing-dot-bubble" w="6px" h="6px" bg="gray.300" rounded="full" />
                      <Box className="typing-dot-bubble" w="6px" h="6px" bg="gray.300" rounded="full" style={{animationDelay: "0.2s"}} />
                      <Box className="typing-dot-bubble" w="6px" h="6px" bg="gray.300" rounded="full" style={{animationDelay: "0.4s"}} />
                    </HStack>
                  </Box>
                </Flex>
              </Flex>
            )}
          </VStack>
        )}
      </Box>

      {/* Message Input */}
      <Input
        type="file"
        ref={fileInputRef}
        hidden
        multiple
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.txt"
      />

      <Box borderTop="1px solid" borderColor="gray.100" flexShrink={0} bg="white">
        {canSendMessage ? (
          <SimpleRichTextEditor
            onSendMessage={handleSendMessage}
            onFileAttach={() => fileInputRef.current?.click()}
            onFilesDropped={handleFilesDropped}
            onPatientFilesClick={() => {}}
            showPatientFiles={false}
            showAttachmentButton={true}
            uploadingFile={uploadingFile}
            isSending={isSending}
            onTyping={() => {}}
            placeholder={
              user.isGroup
                ? `Message #${user.displayName}`
                : `Message ${clinicianData?.name}`
            }
            selectedFiles={selectedFiles}
            onRemoveFile={(index) => {
              if (index !== undefined) {
                setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
              } else {
                setSelectedFiles([]);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }
            }}
            getFileIcon={getFileIcon}
            formatFileSize={formatFileSize}
          />
        ) : (
          <Flex p={3} bg="gray.50" align="center" justify="center">
            <Text fontSize="13px" color="gray.500">
              You can only respond to conversations sent to you.
            </Text>
          </Flex>
        )}
      </Box>

      <style>{`
        @keyframes typingBubble {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.85); }
          30% { opacity: 1; transform: scale(1); }
        }
        .typing-dot-bubble {
          animation: typingBubble 1.4s infinite;
        }
        @keyframes messageFadeIn {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .message-fade-in {
          animation: messageFadeIn 0.2s ease-out;
        }
      `}</style>

      {/* File Preview Modal */}
      {filePreview && (
        <MessagingFilePreview
          preview={filePreview}
          setPreview={setFilePreview}
          state={schState}
        />
      )}
    </Flex>
  );
}
